import {
  Editor,
  Element as SlateElement,
  Node as SlateNode,
  type Point,
  Range,
  Text,
  Transforms as SlateTransforms,
} from 'slate';

import { DEFAULT_SLATE_VALUE, SLATE_TEXT_FORMATS } from './const';
import { htmlToSlate, textToSlate } from './deserialize';
import { createSlateRange, isSlateLinkActive, isSlateLinkNode } from './queries';
import { placeCursorAtEnd } from './selection';
import type { EditorTextChange, SlateValue } from './types';

export const clearSlateEditor = (editor: Editor) => {
  try {
    SlateTransforms.select(editor, {
      anchor: Editor.start(editor, []),
      focus: Editor.end(editor, []),
    });
    SlateTransforms.delete(editor);
  } catch {
    // editor already empty
  }
};

/**
 * Apply text changes to the Slate editor
 * @param editor - Slate editor instance
 * @param changes - Text changes
 */
export const applyEditorTextChanges = (editor: Editor, changes: EditorTextChange[]): void => {
  changes.forEach((change) => {
    if (!change?.oldText || !change?.newText) {
      return;
    }

    const endIndex =
      change.endIndex != null ? change.endIndex : change.startIndex + change.oldText.length;
    const range = createSlateRange(editor, change.startIndex, endIndex);
    if (!range) {
      return;
    }

    SlateTransforms.insertText(editor, change.newText, {
      at: range,
    });
  });
};

export const replaceSlateEditorValue = (editor: Editor, newEditorValue: SlateValue): void => {
  if (!editor) {
    return;
  }

  // Remove selection, in order to mitigate "can't find node" errors
  SlateTransforms.deselect(editor);

  // The editor is empty between the remove and the insert. A normalizer that refills an empty editor
  // must not run there, or the new value lands after the refill.
  Editor.withoutNormalizing(editor, () => {
    // Remove existing nodes
    const children = [...editor.children];
    children.forEach((node) => editor.apply({ type: 'remove_node', path: [0], node }));

    // Add new nodes (fallback to proper "empty" value if new value is empty or invalid)
    const alignedNewValue =
      Array.isArray(newEditorValue) && newEditorValue.length > 0
        ? newEditorValue
        : DEFAULT_SLATE_VALUE;
    SlateTransforms.insertNodes(editor, alignedNewValue);
  });

  placeCursorAtEnd(editor);
};

const isPointInEditor = (editor: Editor, point: Point): boolean => {
  if (!SlateNode.has(editor, point.path)) {
    return false;
  }

  const node = SlateNode.get(editor, point.path);
  return Text.isText(node) && point.offset <= node.text.length;
};

/**
 * Swaps the editor content without Slate operations, the way `<Slate initialValue>` does on mount.
 * Operations move the selection with the content. A direct swap does not. slate-react reads the
 * selection after each render, so a selection outside the new content throws.
 */
export const replaceSlateEditorChildren = (editor: Editor, children: SlateValue): void => {
  editor.children = children;

  const { selection } = editor;
  if (
    selection &&
    !(isPointInEditor(editor, selection.anchor) && isPointInEditor(editor, selection.focus))
  ) {
    editor.selection = null;
  }
};

export const replaceSlateEditorText = (editor: Editor, newTextValue: string): void => {
  if (!editor || newTextValue == null) {
    return;
  }

  const newValue = textToSlate(newTextValue);
  replaceSlateEditorValue(editor, newValue);
};

export const replaceSlateEditorHtml = (editor: Editor, newHtmlValue: string): void => {
  if (!editor || newHtmlValue == null) {
    return;
  }

  const newValue = htmlToSlate(newHtmlValue);
  replaceSlateEditorValue(editor, newValue);
};

export const toggleSlateLink = (editor: Editor, url: string) => {
  if (isSlateLinkActive(editor)) {
    unwrapSlateLink(editor);
  }

  const { selection } = editor;
  const isCollapsed = selection ? Range.isCollapsed(selection) : true;
  const link = {
    type: SLATE_TEXT_FORMATS.LINK,
    url,
    children: isCollapsed ? [{ text: url }] : [],
  };

  if (isCollapsed) {
    SlateTransforms.insertNodes(editor, link);
  } else {
    SlateTransforms.wrapNodes(editor, link, { split: true });
    SlateTransforms.collapse(editor, { edge: 'end' });
  }
};

export const unwrapSlateLink = (editor: Editor) => {
  SlateTransforms.unwrapNodes(editor, {
    match: (node) =>
      !Editor.isEditor(node) && SlateElement.isElement(node) && isSlateLinkNode(node),
  });
};
